/**
 * **Why the server said no to a new or moved session** — one sentence, and whether the calendar on
 * screen is likely stale enough to read again.
 *
 * A refusal that names itself is worded by its code: `slot_taken` is the double-booking
 * constraint (a single session over another live booking, R9), `midnight_is_not_an_hour` the one
 * hour no writer may choose, `past_booking_stays_behind` a missed booking pushed into the future
 * (R10). A refusal without a code is worded by its status. No answer at all — a dropped connection
 * — is said as such, never as something the server decided.
 *
 * Pure: no React, no DOM, no query hook, no i18next. Words are i18n KEYS.
 */

export interface WriteRefusal {
  readonly key: string;
  /** The calendar on screen may be behind the server; read it again. */
  readonly refetch: boolean;
}

import { CREDIT_REFUSAL_KEYS, type FailedWrite } from '~/features/credit-ledger';

const KEY = 'web:booking.refusal';

const NAMED: Readonly<Record<string, WriteRefusal>> = {
  slot_taken: { key: `${KEY}.slot_taken`, refetch: true },
  midnight_is_not_an_hour: { key: `${KEY}.midnight_is_not_an_hour`, refetch: false },
  past_booking_stays_behind: { key: `${KEY}.past_booking_stays_behind`, refetch: true },
  // The grid's zone is one this server cannot resolve. Not retried without it: the server's own
  // fallback is trainer → client → Warsaw, which is not the clock the trainer picked the hour on.
  unknown_time_zone: { key: `${KEY}.unknown_time_zone`, refetch: false },
  // A forgiveness (R14): credits switched off since the panel was read, or a resend of an entry
  // that already landed.
  credits_disabled: { key: CREDIT_REFUSAL_KEYS.credits_disabled, refetch: true },
  idempotency_key_reused: { key: CREDIT_REFUSAL_KEYS.idempotency_key_reused, refetch: true },
  // Two writes to one session at once: the other one won.
  concurrency_conflict: { key: `${KEY}.concurrency_conflict`, refetch: true },
  // A video call (consultation-calls R1, R2): the link the server would not store, a link on an
  // in-person session, a video call on a workout.
  call_link_invalid: { key: `${KEY}.call_link_invalid`, refetch: false },
  call_link_needs_video_call: { key: `${KEY}.call_link_needs_video_call`, refetch: true },
  video_call_not_a_consultation: { key: `${KEY}.video_call_not_a_consultation`, refetch: true },
  // The link's source (owner, 28.10.2026): a link sent under another source, or Standing with none saved.
  call_link_source_mismatch: { key: `${KEY}.call_link_source_mismatch`, refetch: false },
  standing_call_link_missing: { key: `${KEY}.standing_call_link_missing`, refetch: true },
};

export function writeRefusal(failure: FailedWrite | null): WriteRefusal {
  if (failure === null) return { key: `${KEY}.unreached`, refetch: false };
  const named = failure.code === undefined ? undefined : NAMED[failure.code];
  if (named !== undefined) return named;
  if (failure.status === 403) return { key: `${KEY}.forbidden`, refetch: false };
  if (failure.status === 404) return { key: `${KEY}.gone`, refetch: true };
  if (failure.status === 400 || failure.status === 409 || failure.status === 422) {
    return { key: `${KEY}.invalid`, refetch: true };
  }
  return { key: `${KEY}.other`, refetch: false };
}
